import moduleAccessMenu from "./moduleAccessMenu.json";

export const MODULE_ACCESS_MENU = moduleAccessMenu;

export const ADMIN_CONSOLE_MENU_KEY = "ADMIN_CONSOLE";
export const LEGACY_ADMIN_CONSOLE_MENU_KEY = "EMPLOYEES";
export const ADMIN_CONSOLE_SUB_MENU_KEYS = new Set([
  "MASTER_ITEMS",
  "MODULE_ACCESS",
  "MODULE_ACCESS_LOGS",
]);
const FIBER_MANAGEMENT_SUB_MENU_KEYS = new Set([
  "FIBER_MASTER_DATA",
  "FIBER_MONTHLY_REPORT",
  "FIBER_MAINTENANCE",
  "FIBER_CUT_TRACKER",
  "OTDR_TESTING",
  "FIBER_SPLICING",
  "FIBER_MONITORING",
]);

const normalizeMenuKey = (value) => String(value || "").trim().toUpperCase();
const isAdminConsoleSubMenuKey = (subMenuKey) =>
  ADMIN_CONSOLE_SUB_MENU_KEYS.has(normalizeMenuKey(subMenuKey));

export const normalizeModuleGrantKeys = (mainMenuKey, subMenuKey) => {
  const normalizedMainMenuKey = normalizeMenuKey(mainMenuKey);
  const normalizedSubMenuKey = normalizeMenuKey(subMenuKey);

  if (
    normalizedMainMenuKey === "PREPLAN" &&
    ["DASHBOARD", "PREPLAN_DASHBOARD"].includes(normalizedSubMenuKey)
  ) {
    return {
      mainMenuKey: "CRM",
      subMenuKey: "PREPLAN_DASHBOARD",
    };
  }

  if (normalizedMainMenuKey === "PAYMENT_RENEWAL") {
    return {
      mainMenuKey: "IT",
      subMenuKey: "PAYMENT_RENEWAL_DASHBOARD",
    };
  }

  if (
    normalizedMainMenuKey === "ONM" &&
    FIBER_MANAGEMENT_SUB_MENU_KEYS.has(normalizedSubMenuKey)
  ) {
    return {
      mainMenuKey: "FIBER_MANAGEMENT",
      subMenuKey: normalizedSubMenuKey,
    };
  }

  if (
    normalizedMainMenuKey === LEGACY_ADMIN_CONSOLE_MENU_KEY &&
    isAdminConsoleSubMenuKey(normalizedSubMenuKey)
  ) {
    return {
      mainMenuKey: ADMIN_CONSOLE_MENU_KEY,
      subMenuKey: normalizedSubMenuKey,
    };
  }

  return {
    mainMenuKey: normalizedMainMenuKey,
    subMenuKey: normalizedSubMenuKey,
  };
};

const getModuleGrants = (user) => (Array.isArray(user?.module_access) ? user.module_access : []);

export const MODULE_ACCESS_MAIN_OPTIONS = MODULE_ACCESS_MENU.map(({ key, label }) => ({
  key,
  label,
}));

export const getModuleAccessSubMenus = (mainMenuKey) =>
  MODULE_ACCESS_MENU.find((menu) => menu.key === mainMenuKey)?.subMenus || [];

export const getModuleGrant = (user, mainMenuKey, subMenuKey) => {
  const grants = getModuleGrants(user);
  const normalizedRequested = normalizeModuleGrantKeys(mainMenuKey, subMenuKey);

  return (
    grants.find((grant) => {
      const normalizedGrant = normalizeModuleGrantKeys(
        grant.main_menu_key,
        grant.sub_menu_key
      );

      return (
        normalizedGrant.mainMenuKey === normalizedRequested.mainMenuKey &&
        normalizedGrant.subMenuKey === normalizedRequested.subMenuKey
      );
    }) || null
  );
};

export const hasAnyExplicitModuleGrant = (user) => getModuleGrants(user).length > 0;

export const hasAnyModuleGrantForMenu = (user, mainMenuKey) =>
  getModuleGrants(user).some((grant) => {
    const normalizedGrant = normalizeModuleGrantKeys(
      grant?.main_menu_key,
      grant?.sub_menu_key
    );
    return normalizedGrant.mainMenuKey === normalizeMenuKey(mainMenuKey);
  });

export const hasModulePermission = (user, mainMenuKey, subMenuKey, action = "view") => {
  const role = normalizeMenuKey(user?.role);
  if (role === "ADMIN" || role === "SUPER_ADMIN") return true;

  const matchedGrant = getModuleGrant(user, mainMenuKey, subMenuKey);

  if (!matchedGrant) return false;

  const normalizedAction = normalizeMenuKey(action || "view");

  if (normalizedAction === "CREATE") return Boolean(matchedGrant.can_create);
  if (normalizedAction === "EDIT") return Boolean(matchedGrant.can_edit);
  if (normalizedAction === "DELETE") return Boolean(matchedGrant.can_delete);
  if (normalizedAction === "EXPORT") return Boolean(matchedGrant.can_export);
  if (normalizedAction === "APPROVE") return Boolean(matchedGrant.can_approve);
  return Boolean(
    matchedGrant.can_view ||
    matchedGrant.can_edit ||
    matchedGrant.can_delete ||
    matchedGrant.can_create ||
    matchedGrant.can_export ||
    matchedGrant.can_approve
  );
};

export const resolveModuleAccessRedirectPath = (user, fallbackPath = "/active") => {
  const normalizedRole = normalizeMenuKey(user?.role);

  if (normalizedRole === "ADMIN" || normalizedRole === "SUPER_ADMIN") {
    return "/create";
  }

  if (!hasAnyExplicitModuleGrant(user)) {
    return fallbackPath;
  }

  for (const menu of MODULE_ACCESS_MENU) {
    for (const subMenu of menu.subMenus || []) {
      if (!hasModulePermission(user, menu.key, subMenu.key, "view")) {
        continue;
      }

      const firstNavigableRoute = (subMenu.routes || []).find(
        (route) => typeof route === "string" && route.startsWith("/") && !route.includes("/:")
      );

      if (firstNavigableRoute) {
        return firstNavigableRoute;
      }
    }
  }

  return fallbackPath;
};
